import React from 'react';

import { T } from 'react-translator-component';
import styled from 'styled-components';

import { InviteListItem } from '../../../types/responses/Interoperability';
import StatusBadge from '../../atoms/StatusBadge';
import { directionHint, directionLabel, directionOfInvite } from '../../organisms/Interoperability/contractRules';
import { fullMoment, yesNo } from '../../organisms/Interoperability/detailFormat';
import { authorLabel, canDelete, inviteDeadline, inviteDirectionLabel, inviteStatusLabel, INVITE_STATUS_STYLE } from '../../organisms/Interoperability/InvitesTab/inviteLabels';
import {
  DetailCard,
  DetailHint,
  DetailMono,
  DetailRow,
  DetailTitle,
  DetailValue,
} from '../../organisms/Interoperability/styled';

const Actions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 4px;
`;

const ActionButton = styled.button<{ danger?: boolean }>`
  border: 1px solid ${({ danger }) => (danger ? '#e0b4b4' : '#d6d9de')};
  background: #fff;
  color: ${({ danger }) => (danger ? '#b23b3b' : '#12263f')};
  border-radius: 3px;
  font-size: 13px;
  padding: 6px 14px;
  cursor: pointer;

  &:hover {
    border-color: ${({ danger }) => (danger ? '#b23b3b' : '#8d929e')};
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

export type InviteDrawerDetailsProps = {
  invite: InviteListItem;
  busy?: boolean;

  /** Excluir de vez — só é chamado para convite revogado. A confirmação fica no pai. */
  onDelete?: () => void;
};

/**
 * Detalhe de um convite (MV2-1706).
 *
 * O painel não mostra o token, e nem poderia: ele é gravado como hash e exibido uma
 * única vez, na geração. Dizer isso aqui é parte do detalhe — quem abre o painel
 * procurando o código precisa saber que o caminho é gerar outro convite, e não
 * procurar melhor.
 */
export const InviteDrawerDetails: React.FC<InviteDrawerDetailsProps> = ({ invite, busy, onDelete }) => {
  const style = INVITE_STATUS_STYLE[invite.status];
  const direction = directionOfInvite(invite.contract_type, invite.direction);

  return (
    <div>
      <DetailCard>
        <DetailTitle>{T('Invite')}</DetailTitle>

        <DetailRow>
          {T('Status')}
          <DetailValue>
            <StatusBadge tone={{ bg: style?.bg || '#eef0f2', fg: style?.fg || '#6b7280', dot: style?.dot || '#9aa3af' }}>
              {inviteStatusLabel(invite.status)}
            </StatusBadge>
          </DetailValue>
        </DetailRow>

        <DetailRow>
          {T('Direction')}
          <DetailValue>{inviteDirectionLabel(invite.direction)}</DetailValue>
        </DetailRow>

        <DetailRow>
          {T('Deadline')}
          <DetailValue>{inviteDeadline(invite)}</DetailValue>
        </DetailRow>

        <DetailRow>
          {T('Identifier')}
          <DetailValue>
            <DetailMono>{invite.invite_id}</DetailMono>
          </DetailValue>
        </DetailRow>
      </DetailCard>

      <DetailCard>
        <DetailTitle>{T('Contract')}</DetailTitle>

        <DetailRow>
          {T('Type')}
          <DetailValue>{directionLabel(direction)}</DetailValue>
        </DetailRow>

        <DetailRow>
          {T('Partner')}
          <DetailValue>{invite.partner_name || '—'}</DetailValue>
        </DetailRow>

        <DetailRow>
          {T('Accounts in scope')}
          <DetailValue>{invite.scope_account_count ?? '—'}</DetailValue>
        </DetailRow>

        <DetailRow>
          {T('Includes new sub-accounts')}
          <DetailValue>{yesNo(invite.inherits_new_subaccounts)}</DetailValue>
        </DetailRow>

        <DetailHint>{directionHint(direction)}</DetailHint>
      </DetailCard>

      <DetailCard>
        <DetailTitle>{T('History')}</DetailTitle>

        <DetailRow>
          {T('Created')}
          <DetailValue>{fullMoment(invite.created_at)}</DetailValue>
        </DetailRow>

        {/*
          Autor so aparece do lado que agiu: quem gerou vem nulo no convite recebido, e quem
          aceitou vem nulo no enviado. A linha fica — tirá-la faria parecer que o sistema não
          guarda o dado —, mas o texto diz QUAL dos casos é (MV2-1819): um travessão sozinho
          foi lido como defeito, porque não distingue "não registrado" de "é de outra empresa".
        */}
        <DetailRow>
          {T('Generated by')}
          <DetailValue>{authorLabel(invite.created_by, invite.direction === 'SENT')}</DetailValue>
        </DetailRow>

        <DetailRow>
          {T('Expires at')}
          <DetailValue>{fullMoment(invite.expires_at)}</DetailValue>
        </DetailRow>

        <DetailRow>
          {T('Accepted at')}
          <DetailValue>{fullMoment(invite.used_at)}</DetailValue>
        </DetailRow>

        <DetailRow>
          {T('Accepted by')}
          <DetailValue>{authorLabel(invite.used_by, invite.direction === 'RECEIVED')}</DetailValue>
        </DetailRow>
      </DetailCard>

      <DetailCard>
        <DetailTitle>{T('Token')}</DetailTitle>

        <DetailHint>
          {T(
            'The token is shown only once, when the invite is generated: it is stored as a hash and cannot be displayed again. To hand it over once more, generate a new invite.',
          )}
        </DetailHint>
      </DetailCard>

      {canDelete(invite) && onDelete && (
        <DetailCard>
          <DetailTitle>{T('Actions')}</DetailTitle>

          <Actions>
            <ActionButton type="button" danger disabled={busy} onClick={onDelete}>
              {T('Delete')}
            </ActionButton>
          </Actions>
          <DetailHint>
            {T('Delete removes this revoked invite for good — the audit trail keeps the record.')}
          </DetailHint>
        </DetailCard>
      )}
    </div>
  );
};
